import { useState } from "react";
import { signedUsd } from "../lib/money";
import GraphCanvas, { Sub } from "./GraphCanvas";

// Phase 3 — the advisor's visual output. Two shapes, both driven by a
// compact JSON schema the highlight_graph/show_chart tools emit: a subgraph
// (rendered through the same GraphCanvas the standalone Graph Explorer
// uses — one rendering path, two entry points) and a small chart primitive
// (bar/stat). No charting library — the frontend has stayed dependency-light
// on purpose; this is a few components, not a dependency.

export function InlineGraph({ sub, highlightIds, caption }: {
  sub: Sub; highlightIds: string[]; caption?: string;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  if (!sub.nodes.length) return null;
  return (
    <div className="card bg-panel/60 space-y-1">
      {caption && <div className="text-xs text-slate-400 italic">{caption}</div>}
      <GraphCanvas
        sub={sub}
        hover={hover}
        onHover={setHover}
        selected={selected}
        onSelect={setSelected}
        highlightIds={new Set(highlightIds)}
      />
    </div>
  );
}

// The advisor's tool schema carries no explicit "this is dollars" flag, so
// money-typed visuals are detected from what the LLM titled/labeled them —
// raw unformatted numbers here were Kaizen D9/F3's worst offender.
const MONEY_RE = /\$|p&l|pnl|usd|dollar|equity|profit|loss|drawdown|budget|deployed|cash|expectancy|notional/i;
// A stat card often mixes kinds ("net P&L" beside "win rate") — a label that
// is clearly a rate/count never gets a dollar sign, whatever the title says.
const NOT_MONEY_RE = /%|rate|factor|count|ratio|trades|streak|days?\b/i;
function isMoney(title: string, label: string): boolean {
  if (NOT_MONEY_RE.test(label)) return false;
  return MONEY_RE.test(label) || MONEY_RE.test(title);
}
function fmtValue(v: number, moneyTyped: boolean): string {
  if (moneyTyped) return signedUsd(v);
  return v.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

export function Chart({ chartType, title, labels, values }: {
  chartType: string; title: string; labels: string[]; values: number[];
}) {
  if (chartType === "stat") {
    return (
      <div className="card bg-panel/60 space-y-2">
        <div className="text-xs text-slate-400">{title}</div>
        <div className="grid grid-cols-2 gap-2">
          {labels.map((l, i) => (
            <div key={i} className="rounded-lg border border-edge px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
              <div className={`text-sm font-bold ${values[i] > 0 ? "text-up" : values[i] < 0 ? "text-down" : "text-slate-200"}`}>
                {fmtValue(values[i], isMoney(title, l))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // bar
  const max = Math.max(1e-9, ...values.map((v) => Math.abs(v)));
  return (
    <div className="card bg-panel/60 space-y-2">
      <div className="text-xs text-slate-400">{title}</div>
      <div className="space-y-1.5">
        {labels.map((l, i) => {
          const v = values[i];
          const pct = Math.min(100, (Math.abs(v) / max) * 100);
          const up = v >= 0;
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-16 shrink-0 text-slate-400 truncate">{l}</span>
              <div className="flex-1 h-3 bg-ink rounded overflow-hidden">
                <div className={`h-full ${up ? "bg-up" : "bg-down"}`} style={{ width: `${pct}%` }} />
              </div>
              <span className={`w-16 shrink-0 text-right font-mono ${up ? "text-up" : "text-down"}`}>
                {fmtValue(v, isMoney(title, labels[i]))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
